import { createState, useState as useHookState } from '@hookstate/core'
import { useEffect, useMemo, useState } from 'react'
import { getMap, useGoogleLoadState } from '../../stores/GoogleMapState'
import { KeyJourney } from '../../types/features'
import { MAP_Z_INDEX } from '../../types/JourneyPlannerTypes'
import { geocodeAddressString, toLatLngLiteralOrNull } from '../../util/geometry-utils'
import { fetchRoute, getLiveRoutes, clearLiveRoutes } from '../../util/google-route-loader'
import {
  createStartMarker,
  createEndMarker,
  removeWaypointMarker,
  RouteWaypointMarker,
} from '../../util/route-pins'

const keyJourneyState = createState<KeyJourney | null>(null)

export const useKeyJourneyState = () => {
  return useHookState(keyJourneyState)
}

let bounds: google.maps.LatLngBounds
let startDirectionsMarker: RouteWaypointMarker
let endDirectionsMarker: RouteWaypointMarker
let polylines: google.maps.Polyline[] = []
let borderPolyline: google.maps.Polyline
let paths: { path: google.maps.LatLng; color: string; journeyId: string }[] = []
let startDirectionsMarkerPos: google.maps.LatLngLiteral | null
let endDirectionsMarkerPos: google.maps.LatLngLiteral | null

const COLORS = {
  green: '#49a74e',
  orange: '#f0a43a',
  red: '#bc3832',
  markerFill: '#236fa6',
}

export const useKeyJourneyMap = () => {
  const googleLoadState = useGoogleLoadState()
  const keyJourneyItem = keyJourneyState.get()
  const map = getMap()

  // Get the coords from location string IF set on the keyJourneyItem
  // (GoogleStart/EndLocations are only set on a keyJourneyItem when there are no links)
  const [googleStartLoc, setGoogleStartLoc] = useState<number[]>([])
  const [googleEndLoc, setGoogleEndLoc] = useState<number[]>([])
  useMemo(async () => {
    if (keyJourneyItem?.GoogleStartLocation && keyJourneyItem?.GoogleEndLocation && map) {
      const start = await geocodeAddressString(keyJourneyItem?.GoogleStartLocation)
      const end = await geocodeAddressString(keyJourneyItem?.GoogleEndLocation)
      setGoogleStartLoc(start)
      setGoogleEndLoc(end)
    }
  }, [keyJourneyItem?.GoogleStartLocation, keyJourneyItem?.GoogleEndLocation, map])

  // Get the path between to the two points GoogleStart/EndLocation
  const [kjPath, setKjPath] = useState<google.maps.LatLngLiteral[] | undefined>()
  const kjCacheKey = keyJourneyItem ? `kj-${keyJourneyItem.ID}` : ''
  useMemo(async () => {
    if (keyJourneyItem && googleStartLoc.length && googleEndLoc.length && map) {
      const result = await fetchRoute({
        origin: { lat: googleStartLoc[0], lng: googleStartLoc[1] },
        destination: { lat: googleEndLoc[0], lng: googleEndLoc[1] },
        cacheKey: kjCacheKey || undefined,
      })
      setKjPath(result.routes[0]?.decodedPath)
    }
  }, [keyJourneyItem, googleStartLoc, googleEndLoc, map, kjCacheKey])

  useEffect(() => {
    const googleLoaded = googleLoadState.get()

    if (googleLoaded && map && keyJourneyItem) {
      const journeyIds = keyJourneyItem?.LinkIds?.split(',') ?? [keyJourneyItem.ID]
      const isSingleJourney = !!(journeyIds.length === 1) ?? false

      let links = keyJourneyItem.JourneyLinks
      if (!Array.isArray(keyJourneyItem.JourneyLinks)) {
        links = Object.values(keyJourneyItem.JourneyLinks ?? [])
      }

      const startPoint = links.find((link) => link.properties.LinkId === parseInt(journeyIds[0]))
      const endPoint = links.find((link) => link.properties.LinkId === parseInt(journeyIds[journeyIds.length - 1]))

      const svgMarker = {
        path: 'M 12 12 m -12 -6 a 6 6 0 1 0 12 0 a 6 6 0 1 0 -12 0',
        fillColor: COLORS.markerFill,
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: 'white',
        rotation: 0,
        scale: 1,
        anchor: new google.maps.Point(6, 0),
      }
      journeyIds.forEach((journeyId) => {
        // IF links, then push to paths
        if (links.length) {
          const link = links.find((l) => l.properties.LinkId === parseInt(journeyId))
          link?.geometry.coordinates.forEach((segment) => {
            segment.forEach((startEnd) => {
              paths.push({
                path: new google.maps.LatLng({ lng: startEnd[0], lat: startEnd[1] }),

                color: COLORS[link.properties.Condition],
                journeyId: journeyId,
              })
            })
          })
        }
        // IF NO links, then push googleStart/End location to paths
        if (googleStartLoc && googleEndLoc && isSingleJourney) {
          const googleStartEnd = [googleStartLoc, googleEndLoc]
          googleStartEnd.forEach((startEnd) => {
            if (startEnd.length) {
              paths.push({
                path: new google.maps.LatLng({ lng: startEnd[0], lat: startEnd[1] }),

                color: COLORS[keyJourneyItem.FreeFlowState],
                journeyId: journeyId,
              })
            }
          })
        }
      })

      const renderedJourneyIds: string[] = []

      for (let i = 0; i < paths.length - 1; i++) {
        if (links.length && paths.length) {
          if (
            renderedJourneyIds.includes(paths[i].journeyId) ||
            startPoint?.properties.LinkId === parseInt(paths[i].journeyId)
          ) {
            polylines.push(
              new google.maps.Polyline({
                path: [paths[i].path, paths[i + 1].path],
                strokeColor: paths[i].color,
                strokeOpacity: 1.0,
                strokeWeight: 6,
                map: map,
                zIndex: MAP_Z_INDEX.directionsRenderer,
              }),
            )
          } else {
            polylines.push(
              new google.maps.Polyline({
                path: [paths[i].path, paths[i + 1].path],
                strokeColor: paths[i].color,
                strokeOpacity: 1.0,
                strokeWeight: 6,
                map: map,
                zIndex: MAP_Z_INDEX.directionsRenderer,
                icons: [
                  {
                    icon: svgMarker,
                    offset: '0%',
                  },
                ],
              }),
            )
            renderedJourneyIds.push(paths[i].journeyId)
          }
        }
        if (kjPath && isSingleJourney && paths.length) {
          const baseOptions: google.maps.PolylineOptions = {
            strokeColor: COLORS[keyJourneyItem.FreeFlowState],
            strokeOpacity: 1.0,
            strokeWeight: 6,
            zIndex: MAP_Z_INDEX.directionsRenderer,
          }
          const alreadyRendered =
            keyJourneyItem && renderedJourneyIds.includes(paths[i].journeyId)
          const polylineOptions: google.maps.PolylineOptions = alreadyRendered
            ? baseOptions
            : { ...baseOptions, icons: [{ icon: svgMarker, offset: '0%' }] }

          const liveKjRoute = getLiveRoutes(kjCacheKey)?.[0]
          if (liveKjRoute?.createPolylines) {
            const kjPolylines = liveKjRoute.createPolylines({ polylineOptions })
            kjPolylines.forEach((p) => p.setMap(map))
            polylines.push(...kjPolylines)
          } else {
            polylines.push(
              new google.maps.Polyline({ ...polylineOptions, path: kjPath, map }),
            )
          }
          if (!alreadyRendered) {
            renderedJourneyIds.push(paths[i].journeyId)
          }
        }
      }

      if (paths.length) {
        borderPolyline = new google.maps.Polyline({
          path: paths.map((x) => x.path),
          geodesic: true,
          strokeColor: '#FFFFFF',
          strokeOpacity: 0.8,
          strokeWeight: 10,
          zIndex: MAP_Z_INDEX.directionsRendererBorder,
        })
        borderPolyline.setMap(map)
      }

      // Link coordinates can be empty strings and the geocoded fallback can
      // still be pending, so either source may yield no usable position —
      // skip the pin rather than pass NaN to a marker
      const startPosition =
        toLatLngLiteralOrNull(
          parseFloat(startPoint?.properties.StartLatitude ?? ''),
          parseFloat(startPoint?.properties.StartLongitude ?? ''),
        ) ?? toLatLngLiteralOrNull(googleStartLoc[0], googleStartLoc[1])

      const endPosition =
        toLatLngLiteralOrNull(
          parseFloat(endPoint?.properties.EndLatitude ?? ''),
          parseFloat(endPoint?.properties.EndLongitude ?? ''),
        ) ?? toLatLngLiteralOrNull(googleEndLoc[0], googleEndLoc[1])

      if (startPosition) {
        startDirectionsMarker = createStartMarker(startPosition, map)
      }
      if (endPosition) {
        endDirectionsMarker = createEndMarker(endPosition, map)
      }

      // AdvancedMarkerElement has no getPosition(), so track the positions
      // ourselves for the bounds fitting below
      startDirectionsMarkerPos = startPosition
      endDirectionsMarkerPos = endPosition
    }

    // Check to confirm coords are present before stepping into setting bounds
    if (map && startDirectionsMarkerPos && endDirectionsMarkerPos) {
      bounds = new google.maps.LatLngBounds()

      bounds.extend(startDirectionsMarkerPos)
      bounds.extend(endDirectionsMarkerPos)

      // Zoom map to fit markers with padding of 50:
      map.fitBounds(bounds, 50)
      // Set an offset on desktop to make routes visible from behind popoout panel:
      if (window.innerWidth > 1000) {
        map.panBy(-200, 0)
      }
    }

    return () => {
      if (map) {
        removeWaypointMarker(startDirectionsMarker)
        removeWaypointMarker(endDirectionsMarker)
        if (polylines.length) {
          for (let i = 0; i < polylines.length; i++) {
            polylines[i].setMap(null)
          }
          paths = []
          polylines = []
        }
        if (borderPolyline) {
          borderPolyline.setMap(null)
        }
      }
      if (kjCacheKey) {
        clearLiveRoutes(kjCacheKey)
      }
    }
    // eslint-disable-next-line
  }, [
    googleLoadState.value,
    keyJourneyItem?.ID,
    window.innerWidth,
    startDirectionsMarker,
    endDirectionsMarker,
    googleStartLoc,
    googleEndLoc,
    kjPath,
  ])
}
